// Single source for config, Forces panel and engine

/** What a change rebuilds */
export type PhysicsEffect = 'force' | 'radius' | 'sim' | 'draw' | 'load'

export type PhysicsGroup = 'Forces' | 'Links' | 'Clusters' | 'Layers' | 'Collide' | 'Nodes' | 'Display' | 'Simulation'

interface Spec {
  key: string // YAML path under graph:
  def: number
  min: number
  max: number
  step: number
  int?: boolean
  label: string
  group: PhysicsGroup
  effect: PhysicsEffect
}

// Defaults must match config.example.yml
export const PHYSICS_SPECS = [
  { key: 'center.strength', def: 0.03, min: 0, max: 0.5, step: 0.005, label: 'Center pull', group: 'Forces', effect: 'force' },
  { key: 'repel.supplier', def: -150, min: -3000, max: 0, step: 10, label: 'Supplier repel', group: 'Forces', effect: 'force' },
  { key: 'repel.store', def: -500, min: -5000, max: 0, step: 10, label: 'Store repel', group: 'Forces', effect: 'force' },
  { key: 'repel.invoice', def: -30, min: -1000, max: 0, step: 1, label: 'Invoice repel', group: 'Forces', effect: 'force' },
  { key: 'repel.order', def: -30, min: -1000, max: 0, step: 1, label: 'Order repel', group: 'Forces', effect: 'force' },
  { key: 'repel.theta', def: 1.2, min: 0.3, max: 1.5, step: 0.05, label: 'Repel accuracy (θ)', group: 'Forces', effect: 'force' },
  { key: 'repel.distance_min', def: 1, min: 0.1, max: 50, step: 0.1, label: 'Repel min distance', group: 'Forces', effect: 'force' },
  { key: 'repel.distance_max', def: 800, min: 50, max: 5000, step: 10, label: 'Repel max distance', group: 'Forces', effect: 'force' },

  { key: 'links.iterations', def: 1, min: 1, max: 5, step: 1, int: true, label: 'Link passes', group: 'Links', effect: 'force' },
  { key: 'links.store.strength', def: 0.7, min: 0, max: 2, step: 0.01, label: 'Store pull', group: 'Links', effect: 'force' },
  { key: 'links.store.distance', def: 40, min: 1, max: 500, step: 1, label: 'Store distance', group: 'Links', effect: 'force' },
  { key: 'links.supplier.strength', def: 0.1, min: 0, max: 2, step: 0.01, label: 'Supplier pull', group: 'Links', effect: 'force' },
  { key: 'links.supplier.distance', def: 150, min: 1, max: 1000, step: 1, label: 'Supplier distance', group: 'Links', effect: 'force' },
  { key: 'links.match.max_strength', def: 1, min: 0, max: 2, step: 0.01, label: 'Match pull, same day', group: 'Links', effect: 'force' },
  { key: 'links.match.min_strength', def: 0.05, min: 0, max: 2, step: 0.01, label: 'Match pull, floor', group: 'Links', effect: 'force' },
  { key: 'links.match.half_life_days', def: 7, min: 0.5, max: 90, step: 0.5, label: 'Match half-life, days', group: 'Links', effect: 'force' },
  { key: 'links.match.distance', def: 25, min: 1, max: 500, step: 1, label: 'Match distance', group: 'Links', effect: 'force' },

  { key: 'clusters.strength', def: 0.4, min: 0, max: 2, step: 0.01, label: 'Store separation', group: 'Clusters', effect: 'force' },
  { key: 'clusters.padding', def: 80, min: 0, max: 1000, step: 5, label: 'Gap between stores', group: 'Clusters', effect: 'force' },
  { key: 'clusters.radius_scale', def: 1.5, min: 0.5, max: 4, step: 0.05, label: 'Clump radius scale', group: 'Clusters', effect: 'force' },
  { key: 'clusters.min_radius', def: 40, min: 0, max: 500, step: 5, label: 'Clump min radius', group: 'Clusters', effect: 'force' },

  { key: 'layers.strength', def: 0.08, min: 0, max: 1, step: 0.005, label: 'Conf layer pull', group: 'Layers', effect: 'force' },
  { key: 'layers.spacing', def: 300, min: 0, max: 3000, step: 10, label: 'Layer spacing', group: 'Layers', effect: 'force' },
  { key: 'layers.anchor', def: 0, min: 0, max: 1, step: 0.05, label: 'Per store ↔ whole graph', group: 'Layers', effect: 'force' },

  { key: 'collide.strength', def: 0.7, min: 0, max: 1, step: 0.05, label: 'Overlap removal', group: 'Collide', effect: 'force' },
  { key: 'collide.padding', def: 2, min: 0, max: 30, step: 0.5, label: 'Node gap', group: 'Collide', effect: 'force' },
  { key: 'collide.iterations', def: 1, min: 1, max: 4, step: 1, int: true, label: 'Collide passes', group: 'Collide', effect: 'force' },
  { key: 'collide.every', def: 3, min: 1, max: 10, step: 1, int: true, label: 'Every N ticks', group: 'Collide', effect: 'force' },
  { key: 'collide.max_nodes', def: 5000, min: 0, max: 50000, step: 500, int: true, label: 'Off above nodes', group: 'Collide', effect: 'force' },

  { key: 'nodes.radius.supplier', def: 7, min: 1, max: 40, step: 0.5, label: 'Supplier size', group: 'Nodes', effect: 'radius' },
  { key: 'nodes.radius.store', def: 9, min: 1, max: 40, step: 0.5, label: 'Store size', group: 'Nodes', effect: 'radius' },
  { key: 'nodes.radius.invoice', def: 4, min: 1, max: 40, step: 0.5, label: 'Invoice size', group: 'Nodes', effect: 'radius' },
  { key: 'nodes.radius.order', def: 4, min: 1, max: 40, step: 0.5, label: 'Order size', group: 'Nodes', effect: 'radius' },
  { key: 'nodes.degree_scale', def: 0.6, min: 0, max: 5, step: 0.05, label: 'Size per √link', group: 'Nodes', effect: 'radius' },
  { key: 'nodes.max_radius', def: 30, min: 2, max: 80, step: 1, label: 'Max size', group: 'Nodes', effect: 'radius' },

  { key: 'display.link_width', def: 0.5, min: 0.1, max: 5, step: 0.05, label: 'Link width', group: 'Display', effect: 'draw' },
  { key: 'display.link_alpha', def: 0.35, min: 0.02, max: 1, step: 0.01, label: 'Store/supplier link opacity', group: 'Display', effect: 'draw' },
  { key: 'display.supplier_dash', def: 3, min: 0, max: 20, step: 0.5, label: 'Supplier link dash, px', group: 'Display', effect: 'draw' },
  { key: 'display.max_dpr', def: 1, min: 0.5, max: 4, step: 0.25, label: 'Max pixel ratio, canvas fallback', group: 'Display', effect: 'draw' },
  { key: 'display.label_zoom', def: 1.6, min: 0.1, max: 8, step: 0.1, label: 'Document labels from zoom', group: 'Display', effect: 'draw' },
  { key: 'display.hub_label_zoom', def: 0.4, min: 0.05, max: 8, step: 0.05, label: 'Hub labels from zoom', group: 'Display', effect: 'draw' },
  { key: 'display.label_fade', def: 0.4, min: 0, max: 4, step: 0.1, label: 'Label fade', group: 'Display', effect: 'draw' },
  { key: 'display.max_labels', def: 300, min: 0, max: 5000, step: 50, int: true, label: 'Max labels', group: 'Display', effect: 'draw' },
  { key: 'display.lod_zoom', def: 0.3, min: 0, max: 2, step: 0.05, label: 'Plain shapes, solid links below zoom', group: 'Display', effect: 'draw' },
  { key: 'display.min_zoom', def: 0.05, min: 0.01, max: 1, step: 0.01, label: 'Min zoom', group: 'Display', effect: 'draw' },
  { key: 'display.max_zoom', def: 8, min: 1, max: 40, step: 0.5, label: 'Max zoom', group: 'Display', effect: 'draw' },
  { key: 'display.dim_alpha', def: 0.12, min: 0, max: 1, step: 0.01, label: 'Dimmed opacity', group: 'Display', effect: 'draw' },
  { key: 'display.linked_alpha', def: 0.5, min: 0.05, max: 1, step: 0.05, label: 'Linked-in opacity', group: 'Display', effect: 'draw' },
  { key: 'display.red_span', def: 0.15, min: 0, max: 1, step: 0.01, label: 'Red span below medium', group: 'Display', effect: 'draw' },

  { key: 'simulation.alpha', def: 1, min: 0.05, max: 1, step: 0.05, label: 'Start heat', group: 'Simulation', effect: 'load' },
  { key: 'simulation.alpha_resume', def: 0.3, min: 0, max: 1, step: 0.05, label: 'Resume heat', group: 'Simulation', effect: 'load' },
  { key: 'simulation.alpha_min', def: 0.001, min: 0.0001, max: 0.1, step: 0.0001, label: 'Stop below heat', group: 'Simulation', effect: 'sim' },
  { key: 'simulation.alpha_decay', def: 0.0228, min: 0.001, max: 0.2, step: 0.0005, label: 'Cooling per tick', group: 'Simulation', effect: 'sim' },
  { key: 'simulation.alpha_target', def: 0, min: 0, max: 0.5, step: 0.01, label: 'Resting heat', group: 'Simulation', effect: 'sim' },
  { key: 'simulation.velocity_decay', def: 0.4, min: 0.05, max: 0.95, step: 0.01, label: 'Friction', group: 'Simulation', effect: 'sim' },
  { key: 'simulation.reheat', def: 0.3, min: 0, max: 1, step: 0.05, label: 'Reheat on change', group: 'Simulation', effect: 'load' },
  { key: 'simulation.warmup_ticks', def: 120, min: 0, max: 1000, step: 10, int: true, label: 'Warm-up ticks', group: 'Simulation', effect: 'load' },
  { key: 'simulation.warmup_max_ms', def: 1500, min: 0, max: 10000, step: 100, int: true, label: 'Warm-up max, ms', group: 'Simulation', effect: 'load' },
  { key: 'simulation.frame_budget_ms', def: 12, min: 2, max: 40, step: 1, int: true, label: 'Tick time per update, ms', group: 'Simulation', effect: 'load' },
] as const satisfies readonly Spec[]

export type PhysicsKey = (typeof PHYSICS_SPECS)[number]['key']
export type Physics = Record<PhysicsKey, number>
export type PhysicsOverrides = Partial<Physics>
export type PhysicsSpec = Spec & { key: PhysicsKey }

const SPECS: readonly PhysicsSpec[] = PHYSICS_SPECS
export const SPEC_OF = new Map<string, PhysicsSpec>(SPECS.map((s) => [s.key, s]))

export const PHYSICS_DEFAULTS = Object.fromEntries(SPECS.map((s) => [s.key, s.def])) as Physics

function at(raw: unknown, path: string): unknown {
  let v = raw
  for (const part of path.split('.')) {
    if (v == null || typeof v !== 'object') return undefined
    v = (v as Record<string, unknown>)[part]
  }
  return v
}

function fit(s: PhysicsSpec, v: number): number {
  const n = Math.min(s.max, Math.max(s.min, v))
  return s.int ? Math.round(n) : n
}

/** Keeps min/max pairs ordered */
function settle(p: Physics): Physics {
  if (p['links.match.min_strength'] > p['links.match.max_strength']) p['links.match.min_strength'] = p['links.match.max_strength']
  if (p['display.min_zoom'] >= p['display.max_zoom']) p['display.min_zoom'] = PHYSICS_DEFAULTS['display.min_zoom']
  return p
}

/** Bad values fall back, out-of-range ones clamp */
export function parsePhysics(raw: unknown): Physics {
  const out = { ...PHYSICS_DEFAULTS }
  for (const s of SPECS) {
    const v = at(raw, s.key)
    if (v === undefined) continue
    if (typeof v !== 'number' || !Number.isFinite(v)) {
      console.warn(`config.yml graph.${s.key}: not a number, using ${String(s.def)}`)
      continue
    }
    out[s.key] = fit(s, v)
    if (out[s.key] !== v) console.warn(`config.yml graph.${s.key}: ${String(v)} clamped to ${String(out[s.key])}`)
  }
  return settle(out)
}

/** Keeps known keys, clamped */
export function parseOverrides(raw: unknown): PhysicsOverrides | null {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null
  const out: PhysicsOverrides = {}
  for (const [k, v] of Object.entries(raw)) {
    const s = SPEC_OF.get(k)
    if (s && typeof v === 'number' && Number.isFinite(v)) out[s.key] = fit(s, v)
  }
  return out
}

export function withOverrides(base: Physics, o: PhysicsOverrides): Physics {
  return settle({ ...base, ...o })
}
